import { forwardRef, useId, useRef } from "react";
import { BackgroundLayer } from "./BackgroundLayer";
import { PlacedImage } from "./PlacedImage";
import { FadedPhoto } from "./FadedPhoto";
import { RatingLayer, NameLayer, StatsLayer, ClubNameLayer } from "./TextLayers";
import { regionBadge } from "../../lib/regions";
import { SHIELD_PATH, SHIELD_INSET_PATH, VIEW, VIEWBOX, flagUrl, statsFor, withDefaults, photoBoxFor } from "../../lib/shield";
import { RowLayers } from "./RowLayout";
import { useDataUrl, useFileData } from "../../lib/images";

// Mobile browsers drop very large masked images, so on-screen previews use a lighter copy.
const SCREEN_MAX = 1200;

const PhotoPlaceholder = () => (
  <g data-export-hide opacity=".28" fill="#fff">
    <circle cx="250" cy="150" r="58" />
    <path d="M140,420 C140,290 190,240 250,240 C310,240 360,290 360,420 Z" />
  </g>
);

const Crest = ({ crest, href }) =>
  crest.show && crest.file && href ? (
    <image data-layer="crest" href={href} x="46" y="254" width="56" height="66" preserveAspectRatio="xMidYMid meet" />
  ) : null;

const Flag = ({ flag, href }) =>
  flag.show && href ? (
    <g data-layer="flag">
      <image href={href} x="51" y="212" width="46" height="30" preserveAspectRatio="xMidYMid slice" />
      <rect x="51" y="212" width="46" height="30" fill="none" stroke="#000" strokeOpacity=".25" strokeWidth="1" />
    </g>
  ) : null;

const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);

const Duotone = ({ id, dark, light }) => {
  const d = rgb(dark).map((v) => v * 0.35), l = rgb(light);
  return (
    <filter id={id} colorInterpolationFilters="sRGB">
      <feColorMatrix type="saturate" values="0" />
      <feComponentTransfer>
        {["R", "G", "B"].map((c, i) => { const F = `feFunc${c}`; return <F key={c} type="table" tableValues={`${d[i]} ${l[i]}`} />; })}
      </feComponentTransfer>
    </filter>
  );
};

// Blend modes let the photo melt into the club background; duotone recolours it in club colours.
const BlendedPhoto = ({ photo, href, uid, box, bake, row }) => {
  const mode = photo.blend || "normal";
  const k = photo.blendStrength ?? 1;
  const fade = (el) => (photo.fade ? <g mask={`url(#${uid}-fade)`}><g mask={`url(#${uid}-fadeL)`}>{el}</g></g> : el);
  const img = photo.fade && bake ? <FadedPhoto photo={photo} href={href} box={box} row={row} /> : fade(<PlacedImage placement={photo} box={box} href={href} />);
  if (mode === "normal") return img;
  if (mode === "duotone") return <>{img}<g filter={`url(#${uid}-duo)`} opacity={k}>{img}</g></>;
  return <><g opacity={1 - k}>{img}</g><g style={{ mixBlendMode: mode }} opacity={k}>{img}</g></>;
};

const usePointerDrag = (onDrag) => {
  const drag = useRef(null);
  const pts = useRef(new Set());
  // A second finger means pinch-zoom, so stop moving the photo.
  const track = (e) => { pts.current.add(e.pointerId); if (pts.current.size > 1) drag.current = null; };
  const untrack = (e) => pts.current.delete(e.pointerId);
  const start = (target, origin) => (e) => {
    if (!onDrag || pts.current.size > 1) return;
    const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
    drag.current = { target, origin, sx: e.clientX, sy: e.clientY, k: svg.viewBox.baseVal.width / svg.getBoundingClientRect().width };
    svg.setPointerCapture?.(e.pointerId);
    e.stopPropagation();
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    onDrag(d.target, d.origin.x + (e.clientX - d.sx) * d.k, d.origin.y + (e.clientY - d.sy) * d.k);
  };
  const end = (e) => { drag.current = null; if (e) untrack(e); };
  return { start, move, end, track };
};

// bleed (artwork units): print mode – no clipping, background extended past the cut line, no guide.
export const ShieldPreview = forwardRef(({ design: raw, onDrag, testId = "shield-preview", bleed, title }, ref) => {
  const design = withDefaults(raw);
  const row = design.layout === "row";
  const hasNum = design.player.shirtNumber !== "" && design.player.shirtNumber != null;
  const place = design.player.numberPlacement || "behind";
  const numColor = design.player.numberColor || design.colours.accent;
  const nameNumber = hasNum && place === "name" ? { prefix: design.player.shirtNumber, prefixFill: numColor } : {};
  const PHOTO_BOX = photoBoxFor(design.layout);
  const print = bleed != null;
  const k = print ? Math.max((VIEW.w + 2 * bleed) / VIEW.w, (VIEW.h + 2 * bleed) / VIEW.h) : 1;
  const uid = useId().replace(/:/g, "");
  const { background, colours, photo, player, crest, flag, stats } = design;
  const photoHref = useFileData(photo.file, print ? null : SCREEN_MAX);
  const bgHref = useFileData(background.upload.file);
  const crestHref = useFileData(crest.file);
  const region = regionBadge(design.sport, flag);
  const countryHref = useDataUrl(!region && flag.code ? flagUrl(flag.code) : null);
  const flagHref = region ? region.href : countryHref;
  const { start, move, end, track } = usePointerDrag(onDrag);
  const shadow = `url(#${uid}-ts)`;
  const bgDraggable = background.style === "upload" && background.upload.file;

  return (
    <svg ref={ref} viewBox={print ? `${-bleed} ${-bleed} ${VIEW.w + 2 * bleed} ${VIEW.h + 2 * bleed}` : VIEWBOX} className="h-full w-full select-none" style={{ touchAction: "pan-x pan-y" }}
      role="img" aria-label={title || `Personalised shield preview for ${raw?.player?.name || "a player"}`}
      data-testid={testId} onPointerDownCapture={track} onPointerMove={move} onPointerUp={end} onPointerCancel={end}>
      <defs>
        <clipPath id={`${uid}-clip`}><path d={SHIELD_PATH} /></clipPath>
        <filter id={`${uid}-ts`} x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.4" floodColor="#000" floodOpacity=".45" />
        </filter>
        <linearGradient id={`${uid}-fadeg`} gradientUnits="userSpaceOnUse" x1="0" y1={PHOTO_BOX.y + PHOTO_BOX.h * 0.62} x2="0" y2={PHOTO_BOX.y + PHOTO_BOX.h}>
          <stop offset="0" stopColor="#fff" /><stop offset="1" stopColor="#000" />
        </linearGradient>
        <mask id={`${uid}-fade`} maskUnits="userSpaceOnUse" x="0" y="0" width={VIEW.w} height={VIEW.h}>
          <rect width={VIEW.w} height={VIEW.h} fill={`url(#${uid}-fadeg)`} />
        </mask>
        <linearGradient id={`${uid}-fadel`} gradientUnits="userSpaceOnUse" x1={PHOTO_BOX.x} y1="0" x2={row ? PHOTO_BOX.x + PHOTO_BOX.w : PHOTO_BOX.x + 56} y2="0">
          {row ? <><stop offset="0" stopColor="#000" /><stop offset=".2" stopColor="#fff" /><stop offset=".8" stopColor="#fff" /><stop offset="1" stopColor="#000" /></>
            : <><stop offset="0" stopColor="#000" /><stop offset="1" stopColor="#fff" /></>}
        </linearGradient>
        <mask id={`${uid}-fadeL`} maskUnits="userSpaceOnUse" x="0" y="0" width={VIEW.w} height={VIEW.h}>
          <rect width={VIEW.w} height={VIEW.h} fill={`url(#${uid}-fadel)`} />
        </mask>
        <linearGradient id={`${uid}-panel`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={colours.primary} stopOpacity="0" /><stop offset=".22" stopColor={colours.primary} stopOpacity=".82" />
          <stop offset="1" stopColor={colours.primary} stopOpacity=".92" />
        </linearGradient>
        <Duotone id={`${uid}-duo`} dark={colours.primary} light={colours.secondary} />
        <linearGradient id={`${uid}-shine`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".18" /><stop offset=".45" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g clipPath={print ? undefined : `url(#${uid}-clip)`}>
        <g data-layer="background" transform={print ? `translate(200 322) scale(${k}) translate(-200 -322)` : undefined} onPointerDown={bgDraggable ? start("background", background.upload) : undefined}
          style={{ cursor: bgDraggable ? "move" : "default", touchAction: bgDraggable ? "none" : undefined }}>
          <BackgroundLayer background={background} colours={colours} uid={uid} uploadHref={bgHref} />
        </g>
        {hasNum && place === "behind" && (
          <text data-layer="shirt-number" data-testid="shield-shirt-number" x={row ? 200 : 290} y={row ? 330 : 300} textAnchor="middle"
            fontFamily="'Outfit', sans-serif" fontWeight="900" fontSize="230" fill={numColor} opacity=".42"
            stroke={colours.secondary} strokeOpacity=".5" strokeWidth="2" pointerEvents="none">{player.shirtNumber}</text>
        )}
        <g data-layer="photo" data-testid="shield-photo-layer" onPointerDown={photo.file ? start("photo", photo) : undefined}
          style={{ cursor: photo.file ? "move" : "default", touchAction: photo.file ? "none" : undefined }}>
          {photo.file ? <BlendedPhoto photo={photo} href={photoHref} uid={uid} box={PHOTO_BOX} bake={!print} row={row} /> : <PhotoPlaceholder />}
        </g>
        {design.textPanel !== false && row && <g data-layer="row-panels" fill={colours.primary} opacity=".72" pointerEvents="none">
          <rect x="36" y="104" width="80" height="100" rx="6" /><rect x="284" y="98" width="80" height="108" rx="6" />
        </g>}
        {design.textPanel !== false && !row && <rect data-layer="rating-panel" x="34" y="104" width="80" height="146" rx="6" fill={colours.primary} opacity=".72" pointerEvents="none" />}
        {design.textPanel !== false && <rect data-layer="text-panel" x="0" y="360" width="400" height="284" fill={`url(#${uid}-panel)`} pointerEvents="none" />}
        <path d={SHIELD_PATH} fill={`url(#${uid}-shine)`} pointerEvents="none" />
        <g data-layer="border" pointerEvents="none">
          {print && <path data-layer="border-bleed" d={`M${-bleed},${-bleed} H${VIEW.w + bleed} V${VIEW.h + bleed} H${-bleed} Z ${SHIELD_PATH}`} fillRule="evenodd" fill={colours.border} />}
          <path d={SHIELD_PATH} fill="none" stroke={colours.border} strokeWidth="18" />
          <path d={SHIELD_INSET_PATH} fill="none" stroke={colours.accent} strokeWidth="2" />
        </g>
        <g pointerEvents="none">
          {row ? <RowLayers design={design} flagHref={flagHref} flagCode={region?.code} hideCode={region?.labelled} crestHref={crestHref} filter={shadow} number={nameNumber} /> : <>
            <RatingLayer player={player} color={colours.text} accent={colours.accent} filter={shadow} />
            <Flag flag={flag} href={flagHref} />
            <Crest crest={crest} href={crestHref} />
            <NameLayer player={player} color={colours.text} accent={colours.accent} filter={shadow} number={nameNumber} />
            <StatsLayer stats={stats} defs={statsFor(design.sport)} color={colours.text} accent={colours.accent} filter={shadow} />
            <ClubNameLayer name={player.clubName} color={colours.text} filter={shadow} />
          </>}
          {hasNum && place === "corner" && (
            <text data-testid="shield-shirt-number" x={row ? 324 : 74} y={row ? 246 : 366} textAnchor="middle" fontFamily="'Outfit', sans-serif"
              fontWeight="900" fontSize="44" fill={numColor} filter={shadow}>{player.shirtNumber}</text>
          )}
        </g>
      </g>

      {design.guide && !print && (
        <g data-layer="cut-guide" data-export-hide data-testid="shield-cut-guide" pointerEvents="none">
          <path d={SHIELD_PATH} fill="none" stroke="#EC008C" strokeWidth="1.2" strokeDasharray="6 4" />
        </g>
      )}
    </svg>
  );
});
